// src/style.css
var sheet = new CSSStyleSheet();
sheet.replaceSync(`:host {
  --button-background: var(--ctc-button-background, rgb(255 255 255 / 8%));
  --button-border: var(--ctc-button-border, none);
  --button-color: var(--ctc-button-color, inherit);
  --button-icon-size: var(--ctc-button-icon-size, 1rem);
  --button-inset: var(--ctc-button-inset, 0.5rem 0.5rem auto auto);
  --button-padding: var(--ctc-button-padding, 0.5rem);
  --button-radius: var(--ctc-button-radius, 0.5rem);

  display: block;
  position: relative;
}

slot[name='button'] button {
  background: var(--button-background);
  border: var(--button-border);
  border-radius: var(--button-radius);
  box-sizing: content-box;
  color: var(--button-color);
  cursor: pointer;
  font-size: var(--button-icon-size);
  height: var(--button-icon-size);
  line-height: 1;
  padding: var(--button-padding);
  position: relative;
  width: var(--button-icon-size);
}

#button-wrapper {
  inset: var(--button-inset);
  position: absolute;
}

slot[name='done-icon'] > svg,
slot[name='done-icon']::slotted(*) {
  inset: var(--button-padding);
  position: absolute;
  transform: scale(0);
}

#announcement {
  height: 0;
  overflow: clip;
  position: absolute;
  width: 0;
}
`);
var style_default = sheet;

// src/CopyToClipboard.js
var CopyToClipboard = class extends HTMLElement {
  /**
   * Defines the custom element with provided tag name
   */
  static register(tagName = "copy-to-clipboard") {
    customElements.define(tagName, this);
  }
  #item = void 0;
  #icons = {};
  get item() {
    return this.#item;
  }
  set item(item) {
    this.#item = item;
  }
  connectedCallback() {
    const { buttonLabel = "Copy" } = this.dataset;
    const template = document.createElement("template");
    template.innerHTML = `<slot></slot><div id="button-wrapper" part="button-wrapper"><slot name="button"><button part="button" type="button" aria-label="${buttonLabel}"><slot name="copy-icon"><svg aria-hidden="true" part="copy-icon" xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="8" height="4" x="8" y="2" rx="1" ry="1"/><path d="M8 4H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-2"/><path d="M16 4h2a2 2 0 0 1 2 2v4"/><path d="M21 14H11"/><path d="m15 10-4 4 4 4"/></svg></slot><slot name="done-icon"><svg aria-hidden="true" part="done-icon" xmlns="http://www.w3.org/2000/svg" width="1em" height="1em" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg></slot></button></slot><span id="announcement" part="announcement" aria-live="polite"></span></div>`;
    const shadow = this.attachShadow({ mode: "open" });
    shadow.appendChild(template.content.cloneNode(true));
    shadow.adoptedStyleSheets.push(style_default);
    this.#configureIcon("copy");
    this.#configureIcon("done");
    const buttonSlot = shadow.querySelector('slot[name="button"]');
    buttonSlot.addEventListener("slotchange", () => {
      this.#icons = null;
    });
    buttonSlot?.addEventListener("click", () => {
      const { announcement = "Copied!", resultEventName = "copyResult" } = this.dataset;
      const dataTransfer = new DataTransfer();
      if (this.dispatchEvent(
        new ClipboardEvent("copy", {
          cancelable: true,
          bubbles: true,
          clipboardData: dataTransfer
        })
      )) {
        this.getClipboardData(dataTransfer).then(async (data) => {
          const notice = this.shadowRoot.getElementById("announcement");
          notice.textContent = announcement;
          this.#buttonAnimation().then(() => notice.textContent = "");
          await navigator.clipboard.write([data]);
          this.dispatchEvent(
            new CustomEvent(resultEventName, {
              bubbles: true,
              detail: {
                result: "success",
                data
              }
            })
          );
        }).catch((err) => {
          this.dispatchEvent(
            new CustomEvent(resultEventName, {
              bubbles: true,
              detail: {
                result: "error",
                error: err
              }
            })
          );
        });
      }
    });
  }
  async getClipboardData(dataTransfer) {
    let item = this.#item;
    if (dataTransfer?.getData("text/plain")) {
      item = new ClipboardItem({
        "text/plain": dataTransfer.getData("text/plain")
      });
    }
    if (!item) {
      if (this.dataset?.url) {
        const data = await fetch(this.dataset.url);
        const blob = await data.blob();
        item = new ClipboardItem({
          [blob.type]: blob
        });
      } else {
        item = new ClipboardItem({
          "text/plain": this.dataset.text ?? this.shadowRoot.querySelector("slot")?.assignedNodes().map((n) => n.textContent).join("").trim()
        });
      }
    }
    return item;
  }
  #configureIcon(name) {
    this.#icons[name] = this.shadowRoot.querySelector(
      `slot[name="${name}-icon"] > svg`
    );
    this.shadowRoot.querySelector(`slot[name="${name}-icon"]`)?.addEventListener("slotchange", (ev) => {
      this.#icons[name] = ev.target.assignedElements()[0];
      ev.stopPropagation();
    });
  }
  async #buttonAnimation() {
    const options = { duration: 2400 };
    const offset = [0, 0.1, 0.9];
    await Promise.all([
      this.#icons?.copy?.animate(
        {
          opacity: [1, 0, 0, 1],
          transform: ["scale(1)", "scale(0)", "scale(0)", "scale(1)"],
          offset
        },
        options
      ).finished,
      this.#icons?.done?.animate(
        {
          opacity: [0, 1, 1, 0],
          transform: ["scale(0)", "scale(1)", "scale(1)", "scale(0)"],
          offset
        },
        options
      ).finished
    ]);
  }
};
export {
  CopyToClipboard as default
};
